import React from "react";
import { Button, Layout, Menu } from "antd";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  DashboardOutlined,
  UserOutlined,
  FileTextOutlined,
  EditOutlined,
  FolderOutlined,
  SettingOutlined,
  PictureOutlined,
  BulbOutlined,
  BulbFilled,
  LogoutOutlined,
} from "@ant-design/icons";
import { useTheme } from "@/contexts/ThemeContext";
import { clearAuthToken } from "@/services/auth";

const { Header, Sider, Content } = Layout;

const DashboardLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useTheme();

  const handleLogout = () => {
    clearAuthToken();
    navigate("/login", { replace: true });
  };

  const menuItems = [
    {
      key: "/",
      icon: <DashboardOutlined />,
      label: <Link to="/">Dashboard</Link>,
    },
    {
      key: "/users",
      icon: <UserOutlined />,
      label: <Link to="/users">Users</Link>,
    },
    {
      key: "/articles",
      icon: <FileTextOutlined />,
      label: <Link to="/articles">Articles</Link>,
    },
    {
      key: "/writers",
      icon: <EditOutlined />,
      label: <Link to="/writers">Writers</Link>,
    },
    {
      key: "/categories",
      icon: <FolderOutlined />,
      label: <Link to="/categories">Categories</Link>,
    },
    {
      key: "/settings",
      icon: <SettingOutlined />,
      label: <Link to="/settings">Settings</Link>,
    },
    {
      key: "/banners",
      icon: <PictureOutlined />,
      label: <Link to="/banners">Banners</Link>,
    },
  ];

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={240}
        style={{
          background: isDark ? "#09090B" : "#FFFFFF",
          borderRight: `1px solid ${isDark ? "#27272A" : "#E4E4E7"}`,
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          overflow: "auto",
        }}
      >
        <div
          style={{
            height: "64px",
            display: "flex",
            alignItems: "center",
            padding: "0 24px",
            borderBottom: `1px solid ${isDark ? "#27272A" : "#E4E4E7"}`,
          }}
        >
          <img
            src={
              !isDark
                ? "/assets/images/logo/media24hr-icon.png"
                : "/assets/images/logo/white-logo.png"
            }
            alt="Logo"
            style={{ width: 40, height: 40, marginRight: 12 }}
          />
          <span
            style={{
              fontWeight: 700,
              fontSize: 18,
              fontFamily: "IBM Plex Sans, sans-serif",
              letterSpacing: "-0.02em",
            }}
          >
            Media24hr
          </span>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          style={{
            border: "none",
            background: "transparent",
            marginTop: 16,
          }}
        />
      </Sider>
      <Layout style={{ marginLeft: 240 }}>
        <Header
          style={{
            background: isDark
              ? "rgba(9, 9, 11, 0.8)"
              : "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(12px)",
            borderBottom: `1px solid ${isDark ? "#27272A" : "#E4E4E7"}`,
            padding: "0 32px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <div
            style={{
              fontFamily: "IBM Plex Sans, sans-serif",
              fontSize: 14,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: isDark ? "#A1A1AA" : "#71717A",
            }}
          ></div>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <div
              onClick={toggleTheme}
              style={{
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                fontSize: 20,
                transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.7")}
              onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              data-testid="theme-toggle-button"
            >
              {isDark ? <BulbFilled /> : <BulbOutlined />}
            </div>
            <Button
              onClick={handleLogout}
              icon={<LogoutOutlined />}
              style={{ borderRadius: 0 }}
              data-testid="logout-button"
            >
              Logout
            </Button>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: 0,
                background: isDark ? "#27272A" : "#E4E4E7",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 600,
                fontSize: 12,
              }}
            >
              AD
            </div>
          </div>
        </Header>
        <Content
          style={{
            padding: 32,
            background: isDark ? "#09090B" : "#FFFFFF",
            minHeight: "calc(100vh - 44px)",
          }}
        >
          <div style={{ maxWidth: 1400, margin: "0 auto" }}>{children}</div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default DashboardLayout;
